<template>
  <div
    :id="`menu-${activeLinkList.handle}`"
    class="mega-nav"
    role="region"
    :aria-labelledby="`header-nav-${activeLinkList.handle}`"
  >
    <!-- First Tier -->
    <div class="mega-nav__tier mega-nav__tier--first">
      <ul class="mega-nav__list">
        <template
          v-for="(link, index) of activeLinkList.items"
          :key="index"
        >
          <li>
            <hr v-if="link.title === 'link_divider'">

            <component
              :is="!link?.items?.length ? 'a' : 'button'"
              v-else
              :id="`menu-toggle-${link.handle}`"
              :type="link?.items?.length ? 'button' : null"
              :href="link.url"
              class="mega-nav__item"
              :class="{ [$class('active')]: secondTierLinkList?.handle === link.handle }"
              :aria-expanded="secondTierLinkList?.handle === link.handle"
              :aria-controls="`menu-${link.handle}`"
              :style="{ color: link.color, backgroundColor: link.backgroundColor }"
              @click="throttledSetTierMenu(2, link)"
              @keyup.enter.space="throttledSetTierMenu(2, link)"
            >
              <span
                class="text-body-s-bold"
                v-text="link.title"
              />

              <icon-arrow-right v-if="link?.items?.length" />
            </component>
          </li>
        </template>
      </ul>
    </div>

    <!-- Second Tier -->
    <transition
      name="mega-nav"
      @enter="onMenuEnter"
      @after-leave="onMenuAfterLeave(2)"
    >
      <div
        v-show="showSecondTier"
        :id="`menu-${secondTierLinkList?.handle}`"
        class="mega-nav__tier mega-nav__tier--second"
        role="region"
        :aria-labelledby="`menu-toggle-${secondTierLinkList?.handle}`"
      >
        <ul class="mega-nav__list">
          <template
            v-for="(secondTierLink, secondTierIndex) of secondTierLinkList?.items"
            :key="secondTierIndex"
          >
            <li>
              <hr v-if="secondTierLink.title === 'link_divider'">

              <component
                :is="!secondTierLink?.items?.length ? 'a' : 'button'"
                v-else
                :id="`menu-toggle-${secondTierLink.handle}`"
                :type="secondTierLink?.items?.length ? 'button' : null"
                :href="secondTierLink.url"
                class="mega-nav__item"
                :class="{ [$class('active')]: thirdTierLinkList?.handle === secondTierLink.handle }"
                :aria-expanded="thirdTierLinkList?.handle === secondTierLink.handle"
                :aria-controls="`menu-${secondTierLink.handle}`"
                :style="{
                  color: secondTierLink.color,
                  backgroundColor: secondTierLink.backgroundColor
                }"
                @click="throttledSetTierMenu(3, secondTierLink)"
                @keyup.enter.space="throttledSetTierMenu(3, secondTierLink)"
              >
                <span
                  class="text-body-s-bold"
                  v-text="secondTierLink.title"
                />

                <icon-arrow-right v-if="secondTierLink?.items?.length" />
              </component>
            </li>
          </template>
        </ul>
      </div>
    </transition>

    <!-- Third Tier -->
    <transition
      name="mega-nav"
      @enter="onMenuEnter"
      @after-leave="onMenuAfterLeave(3)"
    >
      <div
        v-show="showThirdTier"
        :id="`menu-${thirdTierLinkList?.handle}`"
        class="mega-nav__tier mega-nav__tier--third"
        role="region"
        :aria-labelledby="`menu-toggle-${thirdTierLinkList?.handle}`"
      >
        <ul class="mega-nav__list">
          <template
            v-for="(thirdTierLink, thirdTierIndex) of thirdTierLinkList?.items"
            :key="thirdTierIndex"
          >
            <li>
              <hr v-if="thirdTierLink.title === 'link_divider'">

              <a
                v-else
                :href="thirdTierLink.url"
                class="mega-nav__item"
                :style="{
                  color: thirdTierLink.color,
                  backgroundColor: thirdTierLink.backgroundColor
                }"
              >
                <span
                  class="text-body-s-bold"
                  v-text="thirdTierLink.title"
                />
              </a>
            </li>
          </template>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Mega nav (mega-nav)
 * -----------------------------------------------------------------------------
 * Desktop navigation menu.
 *
 * @param {Object} overlay - Overlay API.
 * @param {Object} activeLinkList - Shopify linklist.
 *
 */
import { mapActions, mapState } from 'vuex'
import { realThrottle as throttle } from '~/helpers/general'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

export default {
  name: 'MegaNav',

  components: {
    IconArrowRight,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    activeLinkList: {
      type: Object,
      default: null,
    },
  },

  emits: ['mega-nav-closed'],

  data() {
    return {
      showSecondTier: false,
      secondTierLinkList: null,
      showThirdTier: false,
      thirdTierLinkList: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      throttleSetting: (state) => state.index.settings.throttle,
    }),
  },

  watch: {

    /**
     * Watch for secondTierLinkList to clear 3th submenu tier.
     */
    secondTierLinkList() {
      this.showThirdTier = false

      // Update focus trap
      this.overlay.update({ currentElement: document.activeElement })
    },

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        this.closeOverlay('mega-nav')
      }
    },

    /**
     * Watch overlay isActive property.
     */
    'overlay.isActive'() {
      // Hide 2nd and 3thd mega menu tiers when overlay closes.
      if (this.overlay.isActive) {
        return
      }

      this.showSecondTier = false
      this.showThirdTier = false
      this.closeOverlay('mega-nav')
      this.$emit('mega-nav-closed')
    },

    activeLinkList() {
      if (!this.activeLinkList) {
        return
      }

      this.showSecondTier = false
      this.showThirdTier = false

      // Update focus trap
      this.overlay.update({ currentElement: document.activeElement })
    },
  },

  mounted() {
    // Throttled setTierMenu function.
    this.throttledSetTierMenu = throttle(this.setTierMenu, this.throttleSetting)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    /**
     * Set tier menu
     * @param { Number } tier - Tier number index.
     * @param { Object } link - linklist
     */
    setTierMenu(tier, link) {
      if (!link.items.length) {
        return
      }

      switch (tier) {
        case 2:
          if (this.secondTierLinkList?.handle === link.handle) {
            this.showThirdTier = false
            this.showSecondTier = false
            return
          }

          if (link?.items) {
            this.secondTierLinkList = link
            this.showSecondTier = true
          }

          break

        case 3:
          if (this.thirdTierLinkList?.handle === link.handle) {
            this.showThirdTier = false
            return
          }

          if (link?.items) {
            this.thirdTierLinkList = link
            this.showThirdTier = true
          }

          break
      }
    },

    /**
     * Transition onEnter hook.
     */
    onMenuEnter() {
      // Update focus trap
      this.overlay.update({ currentElement: document.activeElement })
    },

    /**
     * Transition onAfterLeave hook.
     */
    onMenuAfterLeave(tier) {
      switch (tier) {
        case 2:
          this.secondTierLinkList = null
          this.showSecondTier = false
          break

        case 3:
          this.thirdTierLinkList = null
          this.showThirdTier = false
          break
      }
    },
  },
}
</script>

<style lang="scss">
@import './mega-nav';
</style>
